import React, { ReactNode, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { theme } from '../../../theme/theme'
import { formatDate } from '../../../helpers/date'
import { Icon, IconChoices } from '../../common/Icon'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useModal } from '../../../hooks/useModal'
import { PeopleModal } from '../PeopleModal'
import { Dropdown, Option } from '../../common/Dropdown'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { CommentEdit } from '../CommentEdit'
import { removeMarkdown, replaceHtml } from '../../../helpers/string'
import { Button, ButtonVariant } from '../../common/Button'
import { Linkify } from '../../common/Linkify'
import { PostAuthor } from '../Post/PostAuthor'
import { PostAvatar } from '../Post/PostAvatar'
import { deleteComment, toggleLike } from '../../../api/comments'
import { isDeniedRequest, isParentDenied } from '../../../api/helpers'
import { peopleQueryKey } from '../../../api/people'
import { CommentRow } from '../../../generated/api/comment-row'
import { PostSociety } from '../../../generated/api/post-row'
import {
  likedComment,
  refreshComment,
  removeComment,
  updateComment,
} from '../commentCache'
import {
  commentedRow,
  refreshPostRow,
  updatePostRow,
  withdrawPost,
} from '../postRowCache'

interface CommentProps {
  comment: CommentRow
  society: PostSociety
  onDeleted: (commentId: string) => void
  innerRef?: React.Ref<HTMLDivElement>
}

export const Comment = ({
  comment,
  society,
  onDeleted,
  innerRef,
}: CommentProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const [editMode, setEditMode] = useState<boolean>(false)
  const {
    show: showLikesModal,
    open: openLikesModal,
    close: closeLikesModal,
  } = useModal()
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()

  const { postId, actions, likes } = comment
  const queryClient = useQueryClient()

  const { mutate: mutateToggleLike, isPending: isLiking } = useMutation({
    mutationFn: ({ like }: { like: boolean }) => toggleLike(comment.id, like),
    meta: { skipGlobalInvalidate: true },
    onMutate: ({ like }) => {
      updateComment(queryClient, postId, comment.id, (row) =>
        likedComment(row, like)
      )
    },
    onError: (_error, { like }) => {
      updateComment(queryClient, postId, comment.id, (row) =>
        likedComment(row, !like)
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: peopleQueryKey({ kind: 'comment-likers', id: comment.id }),
      })
      refreshComment(queryClient, postId, comment.id).catch((error) => {
        if (isParentDenied(error, comment.id)) {
          withdrawPost(queryClient, postId)
        } else if (isDeniedRequest(error)) {
          removeComment(queryClient, postId, comment.id)
        }
      })
    },
  })

  const { mutate: mutateDeleteComment } = useMutation({
    mutationFn: () => deleteComment(comment.id),
    meta: { skipGlobalInvalidate: true },
    onSuccess: () => {
      removeComment(queryClient, postId, comment.id)
      updatePostRow(queryClient, postId, (row) => commentedRow(row, -1))
      refreshPostRow(queryClient, postId)
      onDeleted(comment.id)
      setToastNotification(
        ToastType.SUCCESS,
        translate('post.flashMessage.deleteCommentSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('post.flashMessage.deleteCommentFailure')
      )
    },
  })

  const processedMessage = removeMarkdown(replaceHtml(comment.message))

  const onToggleLike = (): void => {
    if (!isLiking) {
      mutateToggleLike({ like: !likes.viewerLiked })
    }
  }

  const onLikesClick = (): void => {
    actions.canOpenLikers && openLikesModal()
  }

  const dropdownOptions = [
    ...(actions.canEdit
      ? [
          {
            value: 'edit',
            label: translate('common.actions.edit'),
            onClick: () => setEditMode(true),
            icon: IconChoices.EDIT,
          },
        ]
      : []),
    ...(actions.canDelete
      ? [
          {
            value: 'delete',
            label: translate('common.actions.delete'),
            onClick: openConfirmationModal,
            icon: IconChoices.DELETE_TRASH,
            destructive: true,
          },
        ]
      : []),
  ] as Option[]

  return (
    <div className="flex gap-4" ref={innerRef}>
      <PostAvatar
        role={comment.role}
        author={comment.author}
        society={{ coverUrl: society.coverUrl }}
        avatarSize="sm"
      />
      <div
        style={{ backgroundColor: theme.colors.feedBackground }}
        className="flex w-full max-w-[600px] flex-col rounded-xl p-4"
      >
        <div className="flex justify-between">
          <div className="flex flex-1 flex-col gap-1">
            <div>
              <div>
                <PostAuthor
                  role={comment.role}
                  fullName={comment.author?.fullName}
                  societyType={society.type}
                />
              </div>
              <p className="text-xs">{formatDate(comment.createdAt)}</p>
            </div>
            {editMode ? (
              <CommentEdit comment={comment} setEditMode={setEditMode} />
            ) : (
              <Linkify>
                <p className="whitespace-pre-line">{processedMessage}</p>
              </Linkify>
            )}
            {!editMode && likes.count !== 0 && (
              <div className="mt-2 flex">
                <button
                  className={`${
                    actions.canOpenLikers ? 'hover:underline' : 'cursor-default'
                  }`}
                  onClick={onLikesClick}
                  style={theme.textVariants.caption}
                >
                  {likes.count}{' '}
                  {translate(
                    likes.count === 1
                      ? 'common.singularPlural.like.singular'
                      : 'common.singularPlural.like.plural'
                  )?.toLowerCase()}
                </button>
              </div>
            )}
          </div>
          {!editMode && (
            <div className="flex flex-col items-center gap-5">
              <Icon
                size={20}
                icon={IconChoices.LIKE}
                highlighted={likes.viewerLiked}
                className={
                  actions.canLike ? undefined : 'cursor-not-allowed opacity-50'
                }
                onClick={actions.canLike ? onToggleLike : undefined}
                flexItems="items-start"
              />
              {dropdownOptions.length > 0 && (
                <Dropdown options={dropdownOptions} dropdownContentWidth="w-64">
                  <Button
                    className="bg-transparent hover:bg-neutral-90"
                    variant={ButtonVariant.TEXT}
                    size="xs"
                    icon={IconChoices.MORE}
                    disableTabIndex
                  />
                </Dropdown>
              )}
            </div>
          )}
        </div>
      </div>
      {showLikesModal && (
        <PeopleModal
          icon={IconChoices.HEART}
          iconHighlighted
          title={`${likes.count} ${translate('common.likes').toLowerCase()}`}
          source={{ kind: 'comment-likers', id: comment.id }}
          close={closeLikesModal}
        />
      )}
      <ConfirmationModal
        title={translate('post.alert.commentDeleteTitle')}
        description={translate('post.alert.commentDeleteText')}
        show={showConfirmationModal}
        close={closeConfirmationModal}
        onConfirm={() => mutateDeleteComment()}
      />
    </div>
  )
}
